import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { API } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Flame, CheckCircle2, XCircle, Loader2, MapPin, Calendar } from "lucide-react";

const STATUS_LABEL = {
  aguardando: "Aguardando sua resposta",
  confirmado: "Participação confirmada ✅",
  nao_participara: "Você respondeu que NÃO poderá participar",
};

export default function PSSConvocacaoPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [note, setNote] = useState("");
  const [showNoBox, setShowNoBox] = useState(false);

  const load = () => fetch(`${API}/pss/convocacao/${token}`).then(async (r) => {
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "Convocação não encontrada.");
    return r.json();
  });

  useEffect(() => {
    load().then(setData).catch((e) => toast.error(e.message)).finally(() => setLoading(false));
  }, [token]);

  const respond = async (answer) => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/pss/convocacao/${token}/responder`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ answer, note }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Falha ao enviar resposta.");
      toast.success("Resposta registrada!");
      const d = await load();
      setData(d);
      setShowNoBox(false);
    } catch (e) { toast.error(e.message); }
    finally { setSubmitting(false); }
  };

  if (loading) return (
    <div className="min-h-[60vh] flex items-center justify-center text-forest-900/70">
      <Loader2 className="w-5 h-5 animate-spin mr-2" /> Carregando convocação...
    </div>
  );
  if (!data) return (
    <div className="container mx-auto px-6 py-16 text-center">
      <h1 className="font-display text-3xl">Convocação não encontrada</h1>
      <p className="mt-2 text-forest-900/70">Verifique o link recebido pelo WhatsApp.</p>
    </div>
  );

  const status = data.course_status || "aguardando";
  const already = status !== "aguardando";
  const course = data.course || {};

  return (
    <div className="container mx-auto px-4 sm:px-6 py-8 max-w-3xl" data-testid="pss-convocacao-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-full bg-forest-700 text-cream-100 flex items-center justify-center">
          <Flame className="w-5 h-5" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-forest-700">COOMFLONA · {data.process_code}</div>
          <h1 className="font-display text-3xl md:text-4xl text-forest-900 leading-tight">Convocação para o Curso de Formação</h1>
        </div>
      </div>

      <div className="bg-white border border-cream-400 p-5 mb-4">
        <div className="text-xs uppercase tracking-[0.18em] text-forest-900/70 mb-1">Candidato</div>
        <div className="font-display text-2xl text-forest-900">{data.name}</div>
        <div className="text-sm text-forest-900/70">{data.community || "—"} · {data.phone_display}</div>
      </div>

      <div className="bg-forest-700 text-cream-100 p-5 mb-4" data-testid="course-info">
        <div className="text-xs uppercase tracking-[0.18em] text-copaiba-300 mb-2">Convocação</div>
        <h2 className="font-display text-2xl md:text-3xl mb-3">{course.titulo}</h2>
        <div className="space-y-1 text-sm">
          <div className="flex items-center gap-2"><Calendar className="w-4 h-4" /> <b>Período:</b> {course.periodo}</div>
          <div className="flex items-center gap-2"><MapPin className="w-4 h-4" /> <b>Local:</b> {course.local}</div>
        </div>
      </div>

      <div className="bg-terracotta-500/10 border-2 border-terracotta-500 p-4 mb-5 text-sm" data-testid="course-disclaimer">
        <div className="font-medium text-terracotta-500 mb-1">⚠️ IMPORTANTE — Leia com atenção</div>
        <p className="text-forest-900">{course.disclaimer}</p>
      </div>

      {already ? (
        <div className="bg-cream-100 border border-forest-700/40 p-5 text-center" data-testid="already-answered">
          <div className={`text-lg font-medium ${status === "confirmado" ? "text-forest-700" : "text-terracotta-500"}`}>
            {STATUS_LABEL[status]}
          </div>
          {data.course_responded_at && (
            <div className="text-xs text-forest-900/60 mt-1">
              Resposta enviada em {new Date(data.course_responded_at).toLocaleString("pt-BR")}.
            </div>
          )}
          {data.course_response_note && (
            <div className="text-sm text-forest-900/85 mt-3 italic">&ldquo;{data.course_response_note}&rdquo;</div>
          )}
          <div className="text-xs text-forest-900/60 mt-4">
            Precisa alterar sua resposta? Entre em contato com a COOMFLONA pelo WhatsApp.
          </div>
        </div>
      ) : (
        <div className="grid gap-3">
          <Button
            onClick={() => respond("confirmar")}
            disabled={submitting}
            data-testid="btn-confirmar"
            className="bg-forest-700 hover:bg-forest-900 text-cream-100 rounded-none py-6 text-base"
          >
            <CheckCircle2 className="w-5 h-5 mr-2" />
            🟢 CONFIRMAR PARTICIPAÇÃO NO CURSO
          </Button>

          {!showNoBox ? (
            <Button
              onClick={() => setShowNoBox(true)}
              disabled={submitting}
              data-testid="btn-nao-abrir"
              variant="outline"
              className="border-terracotta-500 text-terracotta-500 hover:bg-terracotta-500 hover:text-white rounded-none py-6 text-base"
            >
              <XCircle className="w-5 h-5 mr-2" />
              🔴 NÃO PODEREI PARTICIPAR
            </Button>
          ) : (
            <div className="bg-white border border-terracotta-500 p-4">
              <div className="text-sm font-medium mb-2">Se possível, informe brevemente o motivo (opcional):</div>
              <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)}
                data-testid="nao-note" placeholder="Ex.: motivo pessoal, saúde, viagem..." className="mb-3" />
              <div className="flex gap-2">
                <Button onClick={() => respond("nao")} disabled={submitting} data-testid="btn-confirm-nao"
                  className="bg-terracotta-500 hover:brightness-90 text-white rounded-none flex-1">
                  Confirmar que NÃO participarei
                </Button>
                <Button onClick={() => setShowNoBox(false)} disabled={submitting} variant="outline" className="rounded-none">
                  Voltar
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
